import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

// Plain text is read in the browser; PDF / Word go to the backend for text extraction.
export const readFileText = async (f) => {
  if (/\.(txt|md)$/i.test(f.name) || f.type.startsWith("text/")) return f.text();
  const fd = new FormData(); fd.append("file", f);
  const { data } = await api.post("/admin/rcc/li/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data?.text || "";
};

export const UploadText = ({ onText, id, label = "Upload file" }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (f) => {
    if (!f) return; setBusy(true);
    try { const t = await readFileText(f); if (!t.trim()) toast.error("No text found in that file."); else { onText(t.trim()); toast.success(`Read ${f.name}`); } }
    catch (e) { toast.error(e.response?.status === 404 ? "Reading PDF/Word files isn't live yet (spec §36.7). Paste the text or upload a .txt." : errMsg(e)); }
    finally { setBusy(false); ref.current.value = ""; }
  };
  return <>
    <input ref={ref} type="file" accept=".txt,.md,.pdf,.doc,.docx,text/plain,application/pdf" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid={`${id}-file`} />
    <Button type="button" size="sm" variant="outline" onClick={() => ref.current.click()} disabled={busy} className="h-8 border-[#27272A]" data-testid={`${id}-upload`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}{label}</Button>
  </>;
};

export const JobAnchor = ({ p, setP, required }) => (
  <div className="space-y-2 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-3" data-testid="li-job">
    <p className="text-[10px] uppercase tracking-wider text-[#F3C649]">Anchor to a job they're hiring for{required ? " *" : " (optional, makes emails far more personal)"}</p>
    <div className="flex flex-wrap gap-2"><Input value={p.job_title || ""} onChange={(e) => setP({ ...p, job_title: e.target.value })} placeholder="Job title, e.g. Senior Solidity Engineer" className="h-8 min-w-[200px] flex-1 text-xs" data-testid="li-job-title" />
      <Input value={p.job_url || ""} onChange={(e) => setP({ ...p, job_url: e.target.value })} placeholder="Job post URL (optional)" className="h-8 min-w-[200px] flex-1 text-xs" data-testid="li-job-url" /></div>
    <Textarea rows={4} value={p.job_text || ""} onChange={(e) => setP({ ...p, job_text: e.target.value })} placeholder="Paste the job description. AI pulls the must-haves and mirrors their language." className="text-xs" data-testid="li-job-text" />
    <UploadText id="li-job" label="Upload job description" onText={(t) => setP((x) => ({ ...x, job_text: t, job_title: x.job_title || t.split("\n").find((l) => l.trim())?.slice(0, 80) || "" }))} />
  </div>
);
